import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = [
  "#D0A84C",
  "#6B8AFF",
  "#4FD1A5",
  "#F472B6",
  "#FB923C",
  "#A78BFA",
  "#34D399",
  "#F87171",
];

interface AutoChartProps {
  rows: Record<string, unknown>[];
}

type ChartType = "bar" | "line" | "pie" | "grouped-bar" | "none";

interface ChartConfig {
  type: ChartType;
  labelKey: string;
  valueKeys: string[];
  groupKey?: string;
  data: Record<string, unknown>[];
}

/** Date/time patterns that suggest a line chart */
const TIME_PATTERNS =
  /^(\d{4}-\d{2}|jan|feb|mar|apr|mai|jun|jul|aug|sep|okt|nov|des|january|february|march|april|may|june|july|august|september|october|november|december|\d{4}-\d{2}-\d{2})/i;

const MONTH_NAMES_NO: Record<string, number> = {
  januar: 1, februar: 2, mars: 3, april: 4, mai: 5, juni: 6,
  juli: 7, august: 8, september: 9, oktober: 10, november: 11, desember: 12,
};

function isNumeric(val: unknown): boolean {
  if (typeof val === "number") return true;
  if (typeof val === "string") {
    const trimmed = val.trim();
    return trimmed !== "" && !isNaN(Number(trimmed));
  }
  return false;
}

function toNumber(val: unknown): number {
  if (typeof val === "number") return val;
  return Number(val);
}

function looksLikeTimeSeries(values: unknown[]): boolean {
  const sample = values.slice(0, 5);
  return sample.every((v) => typeof v === "string" && TIME_PATTERNS.test(v));
}

function analyzeColumns(rows: Record<string, unknown>[]): {
  labelCols: string[];
  numericCols: string[];
} {
  const keys = Object.keys(rows[0]);
  const labelCols: string[] = [];
  const numericCols: string[] = [];

  for (const key of keys) {
    // Sample first few non-null values
    const sample = rows
      .map((r) => r[key])
      .filter((v) => v != null)
      .slice(0, 10);

    if (sample.length === 0) continue;

    if (sample.every((v) => isNumeric(v))) {
      numericCols.push(key);
    } else {
      labelCols.push(key);
    }
  }

  return { labelCols, numericCols };
}

function detectChartConfig(rows: Record<string, unknown>[]): ChartConfig {
  if (rows.length === 0 || rows.length > 200) {
    return { type: "none", labelKey: "", valueKeys: [], data: [] };
  }

  const { labelCols, numericCols } = analyzeColumns(rows);

  if (numericCols.length === 0) {
    return { type: "none", labelKey: "", valueKeys: [], data: [] };
  }

  // Prepare numeric data
  const data = rows.map((row) => {
    const d: Record<string, unknown> = {};
    for (const key of Object.keys(row)) {
      if (numericCols.includes(key)) {
        d[key] = toNumber(row[key]);
      } else {
        d[key] = row[key];
      }
    }
    return d;
  });

  // Single numeric column with no labels → nothing useful to chart
  if (labelCols.length === 0 && numericCols.length === 1) {
    return { type: "none", labelKey: "", valueKeys: [], data: [] };
  }

  const labelKey = labelCols[0] ?? "";

  // If only 1 label col and 1 numeric col with few rows → pie chart
  if (
    labelCols.length === 1 &&
    numericCols.length === 1 &&
    rows.length <= 8 &&
    rows.length >= 2
  ) {
    const labelValues = rows.map((r) => r[labelKey]);
    if (looksLikeTimeSeries(labelValues)) {
      return { type: "line", labelKey, valueKeys: numericCols, data };
    }
    return { type: "bar", labelKey, valueKeys: numericCols, data };
  }

  // Time series → line chart
  if (labelCols.length >= 1) {
    const labelValues = rows.map((r) => r[labelKey]);
    if (looksLikeTimeSeries(labelValues)) {
      return { type: "line", labelKey, valueKeys: numericCols, data };
    }
  }

  // Multiple label columns → might be grouped (e.g. gender + age group)
  if (labelCols.length >= 2 && numericCols.length >= 1) {
    // Pivot: use first label as X axis, second as group
    const groupKey = labelCols[1];
    const valueKey = numericCols[0];
    const groups = [...new Set(rows.map((r) => String(r[groupKey])))];
    const labels = [...new Set(rows.map((r) => String(r[labelKey])))];

    const pivoted = labels.map((label) => {
      const entry: Record<string, unknown> = { [labelKey]: label };
      for (const group of groups) {
        const row = rows.find(
          (r) => String(r[labelKey]) === label && String(r[groupKey]) === group
        );
        entry[group] = row ? toNumber(row[valueKey]) : 0;
      }
      return entry;
    });

    return {
      type: "grouped-bar",
      labelKey,
      valueKeys: groups,
      groupKey,
      data: pivoted,
    };
  }

  // Default: bar chart
  return { type: "bar", labelKey, valueKeys: numericCols, data };
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">
        {label}
      </p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === "number" ? entry.value.toLocaleString("no-NO") : entry.value}
        </p>
      ))}
    </div>
  );
};

export function AutoChart({ rows }: AutoChartProps) {
  const config = detectChartConfig(rows);

  if (config.type === "none") return null;

  const axisStyle = {
    fontSize: 11,
    fill: "rgba(244, 244, 245, 0.60)",
  };

  if (config.type === "line") {
    return (
      <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={config.data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
            <XAxis dataKey={config.labelKey} tick={axisStyle} />
            <YAxis tick={axisStyle} />
            <Tooltip content={<CustomTooltip />} />
            {config.valueKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {config.valueKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={2}
                dot={{ fill: COLORS[i % COLORS.length], r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (config.type === "bar" || config.type === "grouped-bar") {
    return (
      <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={config.data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
            <XAxis dataKey={config.labelKey} tick={axisStyle} />
            <YAxis tick={axisStyle} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            {config.valueKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {config.valueKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                fill={COLORS[i % COLORS.length]}
                radius={[3, 3, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (config.type === "pie") {
    return (
      <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={config.data}
              dataKey={config.valueKeys[0]}
              nameKey={config.labelKey}
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ name, percent }) =>
                `${name} (${(percent * 100).toFixed(0)}%)`
              }
              labelLine={{ stroke: "rgba(244,244,245,0.3)" }}
            >
              {config.data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
}
